import { Injectable, Injector, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, ActivatedRoute, DetachedRouteHandle, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { ReuseTabCached, ReuseTitle, ReuseTabNotify, ReuseTabMatchMode } from './interface';
import { BehaviorSubject, Unsubscribable } from 'rxjs';
import { NaMenuService } from '@ng-arthur/core';
import { NaUtils } from '@ng-arthur/common';

/**
 * è·¯ç±å¤ç¨ç±»ï¼æä¾å¤ç¨æéè¦ä¸äºåºæ¬æ¥å£
 *
 * @export
 * @class NaReuseService
 */
@Injectable({
  providedIn: 'root'
})
export class NaReuseTabService {
  private _debug = true;
  private _max = 10;
  private _mode = ReuseTabMatchMode.Menu;

  private _cachedChange: BehaviorSubject<ReuseTabNotify> = new BehaviorSubject<ReuseTabNotify>(null);
  private _cached: ReuseTabCached[] = [];

  private _titleCached: { [url: string]: ReuseTitle } = {};
  private _closableCached: { [url: string]: boolean } = {};

  private removeUrlBuffer: string;

  private _excludes: RegExp[] = [];

  private _router$: Unsubscribable;

  private _keepingScroll = false;
  keepingScrollContainer: Element;
  private positionBuffer: { [url: string]: [number, number] } = {};

  set keepingScroll(value: boolean) {
    this._keepingScroll = value;
    // this.initScroll();
  }
  get keepingScroll() {
    return this._keepingScroll;
  }

  private get snapshot() {
    return this.injector.get(ActivatedRoute).snapshot;
  }

  /** åè®¸æå¤å¤ç¨å¤å°ä¸ªé¡µé¢ï¼åå¼èå´ `2-100` */
  set max(value: number) {
    this._max = Math.min(Math.max(value, 2), 100);
    for (let i = this._cached.length; i > this._max; i--) {
      this._cached.pop();
    }
  }

  /** è®¾ç½®å¹éæ¨¡å¼ */
  set mode(value: ReuseTabMatchMode) {
    this._mode = value;
  }
  get mode() {
    return this._mode;
  }

  /** è·åå½åç¼å­çè·¯ç±æ»æ° */
  get count() {
    return this._cached.length;
  }

  /** è®¾ç½®Debugæ¨¡å¼ */
  set debug(value: boolean) {
    this._debug = value;
  }
  get debug() {
    return this._debug;
  }

  constructor(private injector: Injector, @Optional() private menuService: NaMenuService) { }

  /** å½åè·¯ç±å°å */
  get curUrl() {
    return NaUtils.getUrl(this.injector.get(ActivatedRoute).snapshot);
  }

  /** èªå®ä¹å½åæ é¢ */
  set title(value: string | ReuseTitle) {
    const url = this.curUrl;
    if (typeof value === 'string') {
      value = { text: value };
    }
    this._titleCached[url] = value;
    this.log('update current tag title: ', value);
    this._cachedChange.next({
      active: 'title',
      title: value,
      list: this._cached,
    });
  }

  /**
   * è·åæ é¢ï¼é¡ºåºå¦ä¸ï¼
   *
   * 1. ç»ä»¶åä½¿ç¨ `ReuseTabService.title = 'new title'` éæ°æå®ææ¬
   * 2. è·¯ç±éç½®ä¸­ data å±æ§ä¸­åå« titleI18n > title
   * 3. èåæ°æ®ä¸­ text å±æ§
   *
   * @param url æå®URL
   * @param route æå®è·¯ç±å¿«ç§
   */
  getTitle(url: string, route?: ActivatedRouteSnapshot): ReuseTitle {
    if (this._titleCached[url]) {
      return this._titleCached[url];
    }

    if (route && route.data && (route.data.titleI18n || route.data.title)) {
      return { text: route.data.title, i18n: route.data.titleI18n } as ReuseTitle;
    }

    const menu = this.mode !== ReuseTabMatchMode.URL ? this.getMenu(url) : null;
    return menu ? { text: menu.text, i18n: menu.i18n } : { text: url };
  }

  /**
   * æ ¹æ®URLè·åèåè¯¦ç»
   *
   * @private
   * @param {string} url
   * @returns
   * @memberof NaReuseService
   */
  private getMenu(url: string) {
    const menus = this.menuService ? this.menuService.getPathByUrl(url) : [];
    if (!menus || menus.length === 0) {
      return null;
    }
    return menus.pop();
  }

  private destroy(handle: any) {
    if (handle && handle.componentRef && handle.componentRef.destroy) {
      handle.componentRef.destroy();
    }
  }

  private log(...args) {
    if (!this.debug) {
      return;
    }
    console.warn(...args);
  }

  /** è·åæå®è·¯å¾ç¼å­æå¨ä½ç½®ï¼`-1` è¡¨ç¤ºæ ç¼å­ */
  index(url: string): number {
    return this._cached.findIndex(w => w.url === url);
  }

  /** è·åæå®è·¯å¾ç¼å­æ¯å¦å­å¨ */
  exists(url: string): boolean {
    return this.index(url) !== -1;
  }

  private remove(url: string | number, includeNonCloseable: boolean): boolean {
    const idx = typeof url === 'string' ? this.index(url) : url;
    const item = idx !== -1 ? this._cached[idx] : null;
    if (!item || (!includeNonCloseable && !item.closable)) {
      return false;
    }

    this.destroy(item._handle);

    this._cached.splice(idx, 1);
    delete this._titleCached[url];
    return true;
  }

  /**
   * æ ¹æ®URLç§»é¤æ ç­¾
   *
   * @param [includeNonCloseable=false] æ¯å¦å¼ºå¶åå«ä¸å¯å³é­
   */
  close(url: string, includeNonCloseable = false) {
    this.removeUrlBuffer = url;
    this.remove(url, includeNonCloseable);
    this._cachedChange.next({ active: 'close', url, list: this._cached });
    this.log('close tag', url);
    return true;
  }
  /** è·åæå®è·¯å¾ç¼å­ */
  get(url: string): ReuseTabCached {
    return url ? this._cached.find(w => w.url === url) || null : null;
  }

  /**
   * è·å `closable` ç¶æï¼é¡ºåºå¦ä¸ï¼
   *
   * 1. ç»ä»¶åä½¿ç¨ `ReuseTabService.closable = true` éæ°æå® `closable` ç¶æ
   * 2. è·¯ç±éç½®ä¸­ data å±æ§ä¸­åå« `reuseClosable`
   * 3. èåæ°æ®ä¸­ `reuseClosable` å±æ§
   *
   * @param url æå®URL
   * @param route æå®è·¯ç±å¿«ç§
   */
  getClosable(url: string, route?: ActivatedRouteSnapshot): boolean {
    if (typeof this._closableCached[url] !== 'undefined') {
      return this._closableCached[url];
    }

    if (route && route.data && typeof route.data.reuseClosable === 'boolean') {
      return route.data.reuseClosable;
    }

    const menu = this.mode !== ReuseTabMatchMode.URL ? this.getMenu(url) : null;
    if (menu && typeof menu.reuseClosable === 'boolean') {
      return menu.reuseClosable;
    }

    return true;
  }

  private runHook(method: string, url: string, comp: any) {
    if (comp.instance && typeof comp.instance[method] === 'function') {
      comp.instance[method]();
    }
  }

  private hasInValidRoute(route: ActivatedRouteSnapshot) {
    return (
      !route.routeConfig ||
      route.routeConfig.loadChildren ||
      route.routeConfig.children
    );
  }

  /**
   * æ£æ¥å¿«ç§æ¯å¦åè®¸è¢«å¤ç¨
   */
  private can(route: ActivatedRouteSnapshot): boolean {
    const url = NaUtils.getUrl(route);
    if (url === this.removeUrlBuffer) {
      return false;
    }
    if (route.data && typeof route.data.reuse === 'boolean') {
      return route.data.reuse;
    }
    if (this.mode !== ReuseTabMatchMode.URL) {
      const menu = this.getMenu(url);
      if (!menu) {
        return false;
      }
      if (this.mode === ReuseTabMatchMode.Menu) {
        if (menu.reuse === false) {
          return false;
        }
      } else {
        if (!menu.reuse || menu.reuse !== true) {
          return false;
        }
      }
      return true;
    }
    return this._excludes.findIndex(r => r.test(url)) === -1;
  }

  /**
   * å·æ°ï¼è§¦åä¸ä¸ª refresh ç±»åäºä»¶
   */
  refresh(data?: any) {
    this._cachedChange.next({ active: 'refresh', data });
  }

  /**
   * 1
   * å³å®æ¯å¦åè®¸è·¯ç±å¤ç¨ï¼è¥ `true` ä¼è§¦å `store`
   * è¡¨ç¤ºå¯¹ææè·¯ç±åè®¸å¤ç¨ å¦æä½ æè·¯ç±ä¸æ³å©ç¨å¯ä»¥å¨è¿å ä¸äºä¸å¡é»è¾å¤æ­ï¼è¿éå¤æ­æ¯å¦ædataæ°æ®å¤æ­æ¯å¦å¤ç¨
   *
   * @param {ActivatedRouteSnapshot} route
   * @returns {boolean}
   * @memberof NaReuseService
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (this.hasInValidRoute(route)) {
      return false;
    }
    this.log('#shouldDetach', this.can(route), NaUtils.getUrl(route));
    return this.can(route);
  }

  /**
   * å­å¨
   * å­å¨: å½è·¯ç±ç¦»å¼æ¶ä¼è§¦åãæpathä½ä¸ºkeyå­å¨è·¯ç±å¿«ç§&ç»ä»¶å½åå®ä¾å¯¹è±¡
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null) {
    const url = NaUtils.getUrl(route);
    const idx = this.index(url);

    const item: ReuseTabCached = {
      title: this.getTitle(url, route),
      closable: this.getClosable(url, route),
      position: this.getKeepingScroll(url, route) ? this.positionBuffer[url] : null,
      url,
      _snapshot: route,
      _handle: handle,
    };
    if (idx === -1) {
      if (this.count >= this._max) {
        // Get the oldest closable location
        const closeIdx = this._cached.findIndex(w => w.closable!);
        if (closeIdx !== -1) {
          this.remove(closeIdx, false);
        }
      }
      this._cached.push(item);
    } else {
      this._cached[idx] = item;
    }
    this.removeUrlBuffer = null;

    this.log('#store', idx === -1 ? '[new]' : '[override]', url);

    // if (handle && handle.componentRef) {
    //   this.runHook('_onReuseDestroy', url, handle.componentRef);
    // }

    this._cachedChange.next({ active: 'add', item, list: this._cached });
  }

  /**
   * å³å®æ¯å¦åè®¸åºç¨ç¼å­æ°æ®
   * è¥ path å¨ç¼å­ä¸­æçé½è®¤ä¸ºåè®¸è¿åè·¯ç±
   * @param route
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (this.hasInValidRoute(route)) {
      return false;
    }
    const url = NaUtils.getUrl(route);
    const data = this.get(url);
    const ret = !!(data && data._handle);
    this.log('#shouldAttach', ret, url);
    if (ret && data!._handle.componentRef) {
      this.runHook('_onReuseInit', url, data!._handle.componentRef);
    }
    return ret;
  }

  /**
   * æåå¤ç¨æ°æ®
   * ä»ç¼å­ä¸­è·åå¿«ç§ï¼è¥æ åè¿ånull
   * @param route
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    if (this.hasInValidRoute(route)) {
      return null;
    }
    const url = NaUtils.getUrl(route);
    const data = this.get(url);
    const ret = !!(data && data._handle);
    this.log('#shouldAttach', ret, url);
    if (ret && data!._handle.componentRef) {
      this.runHook('_onReuseInit', url, data!._handle.componentRef);
    }
    return ret;
  }

   /**
    * å³å®æ¯å¦åºè¯¥è¿è¡å¤ç¨è·¯ç±å¤ç
    * @param future
    * @param curr
    */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    let ret = future.routeConfig === curr.routeConfig;
    if (!ret) {
      return false;
    }
    const path = ((future.routeConfig && future.routeConfig.path) || '') as string;
    if (path.length > 0 && ~path.indexOf(':')) {
      const futureUrl = NaUtils.getUrl(future);
      const currUrl = NaUtils.getUrl(curr);
      ret = futureUrl === currUrl;
    }
    this.log('=====================');
    this.log('#shouldReuseRoute', ret, `${NaUtils.getUrl(curr)}=>${NaUtils.getUrl(future)}`, future, curr);
    return ret;
  }




  /**
   * è·å `keepingScroll` ç¶æï¼é¡ºåºå¦ä¸ï¼
   *
   * 1. è·¯ç±éç½®ä¸­ data å±æ§ä¸­åå« `keepingScroll`
   * 2. èåæ°æ®ä¸­ `keepingScroll` å±æ§
   * 3. ç»ä»¶ `keepingScroll` å¼
   */
  getKeepingScroll(url: string, route?: ActivatedRouteSnapshot): boolean {
    if (route && route.data && typeof route.data.keepingScroll === 'boolean') {
      return route.data.keepingScroll;
    }

    const menu = this.mode !== ReuseTabMatchMode.URL ? this.getMenu(url) : null;
    if (menu && typeof menu.keepingScroll === 'boolean') {
      return menu.keepingScroll;
    }

    return this.keepingScroll;
  }

  // private get ss(): ScrollService {
  //   return this.injector.get(ScrollService);
  // }

  // private initScroll() {
  //   if (this._router$) {
  //     this._router$.unsubscribe();
  //   }

  //   this._router$ = this.injector.get<Router>(Router).events.subscribe(e => {
  //     if (e instanceof NavigationStart) {
  //       const url = this.curUrl;
  //       if (this.getKeepingScroll(url, NaUtils.getTruthRoute(this.snapshot))) {
  //         this.positionBuffer[url] = this.ss.getScrollPosition(this.keepingScrollContainer);
  //       } else {
  //         delete this.positionBuffer[url];
  //       }
  //     } else if (e instanceof NavigationEnd) {
  //       const url = this.curUrl;
  //       const item = this.get(url);
  //       if (item && item.position && this.getKeepingScroll(url, NaUtils.getTruthRoute(this.snapshot))) {
  //         if (this.isDisabledInRouter) {
  //           this.ss.scrollToPosition(this.keepingScrollContainer, item.position);
  //         } else {
  //           setTimeout(() => this.ss.scrollToPosition(this.keepingScrollContainer, item.position!), 1);
  //         }
  //       }
  //     }
  //   });
  // }


}
